import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const graphOptimizationComponent: ComponentDef = {
  id: 'graph-optimization',
  name: 'Graph Optimization',
  description: 'Fuse ops, eliminate dead code, and layout memory to speed up inference and training.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'Pass',
      type: 'select',
      default: 'compilation',
      options: [
        { label: 'Compilation Pipeline', value: 'compilation' },
        { label: 'Op Fusion', value: 'fusion' },
        { label: 'Dead Code Elimination', value: 'dce' },
        { label: 'Memory Planning', value: 'memory' },
        { label: 'Backend Comparison', value: 'backend_comparison' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'compilation') {
      // Full torch.compile pipeline: Python code -> FX tracing -> graph capture -> pattern matching -> operator fusion -> memory planning -> code generation -> optimized kernel
      g.activation('fx_trace', 'x', 'FX Tracing');
      g.activation('graph_capture', 'fx_trace', 'Graph Capture');
      g.activation('pattern_match', 'graph_capture', 'Pattern Matching');
      g.linear('op_fuse', 'pattern_match', [d_model, d_model]);
      g.scale('mem_plan', 'op_fuse', 'Memory Planning');
      g.activation('codegen', 'mem_plan', 'Code Generation');
      g.activation('kernel', 'codegen', 'Optimized Kernel');
      g.linear('out', 'kernel', [d_model, d_model]);
    } else if (variant === 'dce') {
      // Dead Code Elimination: show graph before/after removing unused ops
      g.linear('a', 'x', [d_model, d_model]);
      g.linear('b', 'x', [d_model, d_model]);
      g.linear('dead_op', 'x', [d_model, d_model]);
      g.scale('mark_dead', 'dead_op', 'Mark Unused');
      g.add('add', 'a', 'b');
      g.activation('act', 'add', 'ReLU');
      g.scale('eliminate', 'mark_dead', 'Eliminate');
      g.linear('out', 'act', [d_model, d_model]);
    } else if (variant === 'memory') {
      // Memory Planning: show buffer allocation and reuse
      g.linear('a', 'x', [d_model, d_model]);
      g.linear('b', 'x', [d_model, d_model]);
      g.add('add', 'a', 'b');
      g.activation('act', 'add', 'ReLU');
      g.scale('alloc_plan', 'act', 'Buffer Alloc Plan');
      g.scale('reuse', 'alloc_plan', 'Memory Reuse');
      g.scale('liveness', 'reuse', 'Liveness Analysis');
      g.linear('out', 'liveness', [d_model, d_model]);
    } else if (variant === 'backend_comparison') {
      // Backend Comparison: ONNX RT vs TensorRT vs TVM vs XLA
      g.linear('a', 'x', [d_model, d_model]);
      g.linear('b', 'x', [d_model, d_model]);
      g.add('add', 'a', 'b');
      g.activation('act', 'add', 'ReLU');
      g.split('backends', 'act', [seq_len, d_model], '4 backends');
      g.scale('onnxrt', 'backends', 'ONNX Runtime');
      g.scale('tensorrt', 'backends', 'TensorRT');
      g.scale('tvm', 'backends', 'Apache TVM');
      g.scale('xla', 'backends', 'XLA');
      g.concat('results', ['onnxrt', 'tensorrt', 'tvm', 'xla'], [4]);
      g.metric('latency_cmp', 'results', 'Latency Compare');
      g.linear('out', 'latency_cmp', [d_model, d_model]);
    } else {
      // Op Fusion (default)
      g.linear('a', 'x', [d_model, d_model]);
      g.linear('b', 'x', [d_model, d_model]);
      g.add('add', 'a', 'b');
      g.activation('act', 'add', 'ReLU');
      g.scale('pass', 'act', 'Optimize Pass');
      g.linear('out', 'pass', [d_model, d_model]);
    }

    g.output('out', 'out');
    return g.build();
  }
};
